/**
 * Excel-Styled Report Types (JESS - JSON Excel Style Schema)
 *
 * v6.0.0: TypeScript interfaces for structured LLM output that renders well in Excel.
 * These types mirror the backend Pydantic models in excel_style_models.py.
 */

// ============================================================
// STYLING PRIMITIVES
// ============================================================

export interface FontStyle {
  size?: number; // 8-72
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  color?: string; // Hex e.g. "#1F4788"
  name?: string; // Font name
}

export interface FillStyle {
  color?: string; // Hex background color
  pattern?: "solid" | "none";
}

export interface BorderEdgeStyle {
  style?: "thin" | "medium" | "thick" | "dashed" | "dotted" | "none";
  color?: string;
}

export interface Borders {
  top?: BorderEdgeStyle;
  bottom?: BorderEdgeStyle;
  left?: BorderEdgeStyle;
  right?: BorderEdgeStyle;
  all?: BorderEdgeStyle; // Shorthand for all edges
}

export interface Alignment {
  horizontal?: "left" | "center" | "right" | "justify";
  vertical?: "top" | "middle" | "bottom";
  wrapText?: boolean;
  indent?: number; // 0-10
}

export interface CellStyle {
  font?: FontStyle;
  fill?: FillStyle;
  borders?: Borders;
  alignment?: Alignment;
  numberFormat?: string;
  rowHeight?: number; // pixels
  mergeCols?: number; // columns to merge
}

// ============================================================
// CONTENT ELEMENTS
// ============================================================

export type BadgeType = "critical" | "warning" | "info" | "good" | "neutral";

export interface Badge {
  text: string;
  type: BadgeType;
}

export interface MetricCard {
  label: string;
  value: string;
  badge?: Badge;
  description?: string;
  style_preset?: "default" | "primary" | "success" | "warning" | "danger";
}

export interface ListItem {
  text: string;
  badge?: Badge;
  indent_level?: number; // 0-3
}

export interface Paragraph {
  text: string;
  style?: "normal" | "callout" | "quote" | "highlight";
  alignment?: "left" | "center" | "right";
}

export interface DataTable {
  headers: string[];
  rows: string[][];
  style_preset?: "default" | "striped" | "bordered" | "minimal";
  highlight_first_row?: boolean;
  highlight_column?: number;
}

// ============================================================
// SECTION TYPES
// ============================================================

export type SectionStylePreset = "default" | "highlight" | "warning" | "info" | "rank1" | "rank2";

export type SectionIcon =
  | "chart"
  | "lightbulb"
  | "target"
  | "warning"
  | "check"
  | "list"
  | "table"
  | "trend"
  | "shield";

export interface Section {
  title: string;
  icon?: SectionIcon | string; // Allow string for flexibility
  style_preset?: SectionStylePreset;
  summary?: string;
  tag_insight?: string;
  seasonality_note?: string; // Per-section seasonal adjustment note (above metric cards)
  paragraphs?: Paragraph[];
  metrics?: MetricCard[];
  list_items?: ListItem[];
  table?: DataTable;
}

// ============================================================
// TOP-LEVEL REPORT
// ============================================================

export interface SeasonalitySummaryItem {
  asin: string;
  summary: string;
}

export interface ExcelStyledReport {
  executive_summary: string;
  sections: Section[];
  top_metrics?: MetricCard[];
  seasonality_summary?: SeasonalitySummaryItem[];
  generated_at?: string;
  data_source?: string;
  start_date?: string;
  end_date?: string;
  report_week?: string;
}

// ============================================================
// BADGE COLOR MAPPING (for Excel rendering)
// ============================================================

export const BADGE_COLORS: Record<BadgeType, { bg: string; text: string; border: string }> = {
  critical: { bg: "#FEE2E2", text: "#991B1B", border: "#FECACA" },
  warning: { bg: "#FEF3C7", text: "#92400E", border: "#FDE68A" },
  info: { bg: "#DBEAFE", text: "#1E40AF", border: "#BFDBFE" },
  good: { bg: "#DCFCE7", text: "#166534", border: "#BBF7D0" },
  neutral: { bg: "#F3F4F6", text: "#374151", border: "#E5E7EB" },
};

// ============================================================
// SECTION STYLE PRESET COLORS
// ============================================================

export const SECTION_PRESET_COLORS: Record<
  SectionStylePreset,
  { header: string; headerText: string; body: string; accent: string; border: string }
> = {
  default: {
    header: "#068E75",
    headerText: "#FFFFFF",
    body: "#F0FDF9",
    accent: "#065F46",
    border: "#A7F3D0",
  },
  highlight: {
    header: "#7C3AED",
    headerText: "#FFFFFF",
    body: "#F5F3FF",
    accent: "#6D28D9",
    border: "#C4B5FD",
  },
  warning: {
    header: "#D97706",
    headerText: "#FFFFFF",
    body: "#FFFBEB",
    accent: "#92400E",
    border: "#FDE68A",
  },
  info: {
    header: "#0284C7",
    headerText: "#FFFFFF",
    body: "#F0F9FF",
    accent: "#0369A1",
    border: "#BAE6FD",
  },
  rank1: {
    header: "#3B71CA",
    headerText: "#FFFFFF",
    body: "#A8C7FA",
    accent: "#1E3A5F",
    border: "#7BA8F0",
  },
  rank2: {
    header: "#2E7D52",
    headerText: "#FFFFFF",
    body: "#B7E1C1",
    accent: "#1B4332",
    border: "#86CEAA",
  },
};

// ============================================================
// RANK-HARMONIZED BADGE COLORS (for badges inside rank sections)
// ============================================================

export const RANK_BADGE_COLORS: Record<"rank1" | "rank2", { bg: string; text: string; border: string }> = {
  rank1: { bg: "#C9DDFB", text: "#1E3A5F", border: "#7BA8F0" },
  rank2: { bg: "#CDE9D4", text: "#1B4332", border: "#86CEAA" },
};

/**
 * Detect rank preset from section colors by matching the header color.
 */
export function getRankFromColors(
  colors: { header: string }
): "rank1" | "rank2" | null {
  if (colors.header === SECTION_PRESET_COLORS.rank1.header) return "rank1";
  if (colors.header === SECTION_PRESET_COLORS.rank2.header) return "rank2";
  return null;
}

// ============================================================
// METRIC STYLE PRESET COLORS
// ============================================================

export const METRIC_PRESET_COLORS: Record<
  string,
  { header: string; headerText: string; body: string; bodyText: string; border: string }
> = {
  default: {
    header: "#068E75",
    headerText: "#FFFFFF",
    body: "#F0FDF9",
    bodyText: "#065F46",
    border: "#A7F3D0",
  },
  primary: {
    header: "#1E40AF",
    headerText: "#FFFFFF",
    body: "#EFF6FF",
    bodyText: "#1E3A8A",
    border: "#BFDBFE",
  },
  success: {
    header: "#166534",
    headerText: "#FFFFFF",
    body: "#F0FDF4",
    bodyText: "#14532D",
    border: "#BBF7D0",
  },
  warning: {
    header: "#92400E",
    headerText: "#FFFFFF",
    body: "#FFFBEB",
    bodyText: "#78350F",
    border: "#FDE68A",
  },
  danger: {
    header: "#991B1B",
    headerText: "#FFFFFF",
    body: "#FEF2F2",
    bodyText: "#7F1D1D",
    border: "#FECACA",
  },
};

// ============================================================
// SECTION ICON MAPPING (icon name to emoji)
// ============================================================

export const SECTION_ICONS: Record<string, string> = {
  chart: "\uD83D\uDCCA", // 📊
  lightbulb: "\uD83D\uDCA1", // 💡
  target: "\uD83C\uDFAF", // 🎯
  warning: "\u26A0\uFE0F", // ⚠️
  check: "\u2705", // ✅
  list: "\uD83D\uDCDD", // 📝
  table: "\uD83D\uDCC9", // 📉
  trend: "\uD83D\uDCC8", // 📈
  shield: "\uD83D\uDEE1\uFE0F", // 🛡️
};

// ============================================================
// UTILITY FUNCTIONS
// ============================================================

/**
 * Get emoji for section icon name
 */
export function getSectionIcon(icon?: string): string {
  if (!icon) return "";
  return SECTION_ICONS[icon] || icon;
}

/**
 * Get colors for a badge type
 */
export function getBadgeColors(type: BadgeType): { bg: string; text: string; border: string } {
  return BADGE_COLORS[type] || BADGE_COLORS.neutral;
}

/**
 * Get colors for a section preset
 */
export function getSectionColors(preset?: SectionStylePreset): {
  header: string;
  headerText: string;
  body: string;
  accent: string;
  border: string;
} {
  return SECTION_PRESET_COLORS[preset || "default"];
}

/**
 * Get colors for a metric preset
 */
export function getMetricColors(preset?: string): {
  header: string;
  headerText: string;
  body: string;
  bodyText: string;
  border: string;
} {
  return METRIC_PRESET_COLORS[preset || "default"];
}

/**
 * Parse markdown bold (**text**) from a string
 * Returns true if the string contains bold markers
 */
export function containsBold(text: string): boolean {
  if (!text) return false;
  return text.includes("**");
}

/**
 * Strip markdown formatting from text for Excel display
 */
export function stripMarkdown(text: string): string {
  if (!text) return "";
  return text.replace(/\*\*/g, "").replace(/\*/g, "");
}

/**
 * Calculate row height based on text length and column width.
 *
 * Accounts for explicit `\n` line breaks in the text (each paragraph wraps
 * independently), so multi-line content like keyword blocks no longer gets
 * clipped. The `maxHeight` cap is configurable; callers rendering long
 * multi-paragraph content (e.g. list items) can raise it.
 */
export function calculateRowHeight(
  text: string,
  colWidth: number = 80,
  maxHeight: number = 180
): number {
  if (!text) return 22;
  // `colWidth / 6.5` slightly raises the chars-per-line estimate so wide
  // content isn't over-counted; `lines * 15` matches Excel's natural row
  // height for 11pt text (was 18, which added ~3px of slack per line and
  // made long keyword blocks render with a big empty tail).
  const charsPerLine = Math.max(1, Math.floor(colWidth / 6.5));
  const lines = text.split("\n").reduce((sum, line) => {
    return sum + Math.max(1, Math.ceil(line.length / charsPerLine));
  }, 0);
  return Math.max(22, Math.min(lines * 15, maxHeight));
}
